/**
 * Import d'un planning Excel — la transformation de ce qui a été lu en une semaine de planning (§6).
 *
 * L'import ne touche à rien avant la validation de l'étape 3. Cette fonction ne fait que **préparer** la
 * semaine et son bilan : c'est l'appelant qui décide de l'écrire.
 *
 * Une semaine de l'IAmani n'est pas une table de créneaux mais un seul objet `Planning` enregistré tel
 * quel (`planning_weeks.payload_json`). Un créneau de nuit s'y note donc `19:00 → 01:00` sur la journée
 * de départ : c'est la convention déjà en place, que `heuresDuCreneau` sait relire (une fin antérieure au
 * début vaut « le lendemain »). Jamais de durée négative, jamais trente heures.
 */
import type { ExtraShift, Planning, PlanningRow, Shift, ShiftSegment } from '../../components/Planning/types';
import { getShiftCounts } from '../../components/Planning/shiftServiceUtils';
import type { SemaineLue, Rapprochement, BilanImport, CodeAbsence, ModeEcriture, LectureCellule, RenfortPropose } from './types';
import { lireRenfort, normaliser } from './lireCellule';
import { nomAffiche, type SalarieIAmani } from './rapprocherSalaries';

export interface OptionsConstruction {
    semaine: SemaineLue;
    rapprochements: Rapprochement[];
    /** La semaine déjà enregistrée dans l'IAmani, quand il y en a une. */
    planningExistant: Planning | null;
    mode: ModeEcriture;
    codesConnus: CodeAbsence[];
    salaries: SalarieIAmani[];
    /** L'identifiant du lot, porté par les codes qu'il crée. */
    lotId: string;
}

export interface ResultatConstruction {
    planning: Planning;
    bilan: BilanImport;
    /** Les codes à ajouter aux réglages, à enregistrer en même temps que la semaine. */
    codesACreer: CodeAbsence[];
}

/** Vrai quand la journée déjà enregistrée ne porte rien : c'est un trou que « compléter » peut combler. */
function journeeVide(shift: Shift | undefined): boolean {
    return !shift || !Array.isArray(shift.segments) || shift.segments.length === 0;
}

/** Une journée qui porte au moins un créneau de service. Sert à compter ce qui existe déjà. */
function porteUnCreneau(shift: Shift | undefined): boolean {
    return Boolean(shift?.segments?.some((s) => s.type === 'horaire' && s.start && s.end));
}

/** Assemble les segments d'une journée à partir de ce qui a été lu dans les deux lignes du salarié. */
function segmentsDeLaJournee(lectures: LectureCellule[]): ShiftSegment[] {
    const segments: ShiftSegment[] = [];
    for (const lecture of lectures) {
        if (lecture.genre === 'creneau') {
            segments.push({ type: 'horaire', start: lecture.debut, end: lecture.fin, ...(lecture.note ? { note: lecture.note } : {}) });
        } else if (lecture.genre === 'absence') {
            segments.push({ type: 'code', label: lecture.code });
        } else if (lecture.genre === 'renfort' || lecture.genre === 'note') {
            // Ni créneau ni absence : le texte est conservé tel quel sur la journée pour que rien ne se
            // perde. Sans segment horaire, il ne pèse aucune heure et ne compte pour aucun service.
            segments.push({ type: 'code', label: lecture.texte, note: lecture.texte });
        }
    }
    return segments;
}

/** Le type de la journée, dans le vocabulaire du planning. */
function typeDeLaJournee(segments: ShiftSegment[]): Shift['type'] {
    if (segments.some((s) => s.type === 'horaire')) return 'travail';
    const codes = segments.filter((s) => s.type === 'code');
    if (!codes.length) return 'travail';
    return codes.some((s) => normaliser(s.label || '') === 'REPOS') ? 'repos' : 'absence';
}

export function construireSemaine(options: OptionsConstruction): ResultatConstruction {
    const { semaine, rapprochements, planningExistant, mode, codesConnus, salaries, lotId } = options;
    const dates = semaine.dates;

    const parNomFichier = new Map(rapprochements.filter((r) => r.retenu && r.employeeId).map((r) => [r.nomFichier, r]));
    const parId = new Map(salaries.map((s) => [String(s.id), s]));
    const codesDejaConnus = new Set(codesConnus.map((c) => normaliser(c.code)));

    const bilan: BilanImport = {
        creneaux: 0, absences: 0, creneauxDeNuit: 0,
        codesCrees: [], renforts: [], notes: [], nomsIgnores: [], creneauxExistants: 0,
    };
    const codesACreer = new Map<string, CodeAbsence>();

    // Ce qui existe déjà sur les sept jours, avant toute écriture.
    for (const ligne of planningExistant?.rows || []) {
        for (const date of dates) if (porteUnCreneau(ligne.shifts?.[date])) bilan.creneauxExistants += 1;
    }

    // Les lignes du fichier, regroupées par salarié retenu. Une ligne non reconnue est écartée entière.
    const journeesParSalarie = new Map<string, Record<string, ShiftSegment[]>>();
    for (const ligne of semaine.lignes) {
        const rapprochement = parNomFichier.get(ligne.nomFichier);
        if (!rapprochement?.employeeId) {
            const nom = ligne.nomFichier.trim();
            if (nom && !bilan.nomsIgnores.includes(nom)) bilan.nomsIgnores.push(nom);
            continue;
        }

        const parDate = journeesParSalarie.get(rapprochement.employeeId) || {};
        for (const date of dates) {
            const lectures = ligne.journees[date] || [];
            if (!lectures.length) continue;

            for (const lecture of lectures) {
                if (lecture.genre === 'creneau') {
                    bilan.creneaux += 1;
                    if (lecture.finLendemain) bilan.creneauxDeNuit += 1;
                } else if (lecture.genre === 'absence') {
                    bilan.absences += 1;
                    if (lecture.inconnu && !codesDejaConnus.has(lecture.code) && !codesACreer.has(lecture.code)) {
                        codesACreer.set(lecture.code, {
                            code: lecture.code,
                            libelle: lecture.libelle,
                            isFullDay: true,
                            autoApply: false,
                            color: '#ffe39b',
                            remunere: false,
                            decompteConges: false,
                            origine: 'import',
                            lotId,
                        });
                    }
                } else if (lecture.genre === 'renfort') {
                    bilan.renforts.push({ date, texte: lecture.texte, nomFichier: ligne.nomFichier.trim() });
                } else if (lecture.genre === 'note') {
                    bilan.notes.push({ date, texte: lecture.texte, nomFichier: ligne.nomFichier.trim() });
                }
            }

            parDate[date] = [...(parDate[date] || []), ...segmentsDeLaJournee(lectures)];
        }
        journeesParSalarie.set(rapprochement.employeeId, parDate);
    }
    bilan.codesCrees = [...codesACreer.values()];

    // La semaine de départ : celle qui existe, ou une semaine neuve.
    const base: Planning = planningExistant
        ? { ...planningExistant, rows: planningExistant.rows.map((r) => ({ ...r, shifts: { ...r.shifts } })) }
        : {
            id: (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}`),
            weekStart: dates[0], weekEnd: dates[6], service: 'Salle', status: 'active',
            rows: [], extraShifts: [], createdAt: Date.now(),
        };

    // « Remplacer la semaine » : les sept jours sont vidés d'abord, pour tout le monde — y compris les
    // salariés absents du fichier. L'annulation du lot les restitue.
    if (mode === 'remplacer') {
        for (const ligne of base.rows) for (const date of dates) delete ligne.shifts[date];
    }

    const codesPourClassement = codesConnus.map((c) => ({ code: c.code }));

    for (const [employeeId, parDate] of journeesParSalarie) {
        let ligne = base.rows.find((r) => String(r.employeeId) === String(employeeId));
        if (!ligne) {
            const salarie = parId.get(String(employeeId));
            ligne = {
                employeeId,
                employeeName: salarie ? nomAffiche(salarie) : '',
                employeeRole: (salarie as any)?.position || 'GENERAL',
                isExtra: false,
                shifts: {},
            } as PlanningRow;
            base.rows.push(ligne);
        }

        for (const date of dates) {
            const segments = parDate[date];
            if (!segments?.length) continue;
            // « Ne compléter que les jours vides » : ce qui est déjà saisi dans l'IAmani est conservé.
            if (mode === 'completer' && !journeeVide(ligne.shifts[date])) continue;

            const shift: Shift = {
                date,
                type: typeDeLaJournee(segments),
                serviceType: 'none',
                segments,
                isManual: true,
            };
            const comptes = getShiftCounts(shift, codesPourClassement);
            shift.serviceType = comptes.midi && comptes.soir ? 'midi+soir' : comptes.midi ? 'midi' : comptes.soir ? 'soir' : 'none';
            ligne.shifts[date] = shift;
        }
    }

    return { planning: { ...base, weekStart: dates[0], weekEnd: dates[6] }, bilan, codesACreer: bilan.codesCrees };
}

/**
 * Les renforts relevés, préremplis pour l'étape 3. Aucun n'est confirmé au départ : c'est l'utilisateur
 * qui coche, ligne par ligne. Un libellé du catalogue est préféré quand il correspond au texte (SECURITE
 * → « Agent de sécurité ») ; sinon le libellé reste tel qu'écrit dans le fichier.
 */
export function proposerRenforts(bilan: BilanImport, typesCatalogue: string[]): RenfortPropose[] {
    return bilan.renforts.map((renfort, index) => {
        const lu = lireRenfort(renfort.texte);
        const cle = normaliser(lu.libelle);
        const duCatalogue = typesCatalogue.find((t) => {
            const n = normaliser(t);
            return n === cle || n.includes(cle) || cle.includes(n);
        });
        return {
            index,
            date: renfort.date,
            texte: renfort.texte,
            effectif: lu.effectif,
            label: duCatalogue || lu.libelle,
            debut: lu.horaire?.debut || '',
            fin: lu.horaire?.fin || '',
            confirme: false,
        };
    });
}

/** Un renfort ne peut être créé que complet : un libellé, et un horaire de début et de fin. */
export function renfortComplet(renfort: RenfortPropose): boolean {
    return Boolean(renfort.label.trim() && /^\d{2}:\d{2}$/.test(renfort.debut) && /^\d{2}:\d{2}$/.test(renfort.fin));
}

/**
 * Ajoute à la semaine les renforts confirmés, dans le même objet que les créneaux : ils partent dans le
 * même lot, et l'annulation du lot les retire avec le reste. Un renfort strictement identique déjà
 * présent (même jour, libellé, horaire et effectif) n'est pas doublé — le cas d'un fichier réimporté.
 */
export function ajouterRenforts(planning: Planning, proposes: RenfortPropose[]): { planning: Planning; crees: number[] } {
    const extraShifts: ExtraShift[] = [...(planning.extraShifts || [])];
    const crees: number[] = [];
    for (const renfort of proposes) {
        if (!renfort.confirme || !renfortComplet(renfort)) continue;
        const label = renfort.label.trim();
        const doublon = extraShifts.some((e) =>
            e.date === renfort.date && e.label === label && e.start === renfort.debut && e.end === renfort.fin && e.count === renfort.effectif,
        );
        if (!doublon) {
            extraShifts.push({
                id: (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${renfort.index}`),
                label, date: renfort.date, start: renfort.debut, end: renfort.fin, count: renfort.effectif,
            });
        }
        crees.push(renfort.index);
    }
    return { planning: { ...planning, extraShifts }, crees };
}
